// Import types for the specific session sanitizers
import { IMkAuthenticatedSession, IMkAuthorizedSession } from '@auth/types'

/**
 * Utility function to sanitize objects by extracting only the properties
 * that exist in the target interface. This provides runtime safety when
 * casting objects between different types.
 *
 * @param source - The source object to sanitize
 * @param targetKeys - Array of keys that should be preserved
 * @returns A new object containing only the specified keys from the source
 */
export function sanitizeObject<T extends Record<string, any>>(source: T, targetKeys: (keyof T)[]): Pick<T, keyof T> {
  const sanitized = {} as Pick<T, keyof T>

  targetKeys.forEach((key) => {
    if (key in source) {
      ;(sanitized as any)[key] = (source as any)[key]
    }
  })

  return sanitized
}

/**
 * Utility function specifically for sanitizing session objects to match
 * the IMkAuthenticatedSession interface.
 *
 * @param source - The source session object
 * @returns A sanitized object matching IMkAuthenticatedSession
 */
export function sanitizeAuthenticatedSession(source: Record<string, any>): IMkAuthenticatedSession {
  const targetKeys: (keyof IMkAuthenticatedSession)[] = ['sessionType', 'authBrand', 'authenticatedUser', 'codeVerifier', 'username', 'sessionToken', 'recoveryToken']

  return sanitizeObject(source, targetKeys) as IMkAuthenticatedSession
}

/**
 * Utility function specifically for sanitizing session objects to match
 * the IMkAuthorizedSession interface.
 *
 * @param source - The source session object
 * @returns A sanitized object matching IMkAuthorizedSession
 */
export function sanitizeAuthorizedSession(source: Record<string, any>): IMkAuthorizedSession {
  const targetKeys: (keyof IMkAuthorizedSession)[] = [
    'sessionType',
    'authBrand',
    'authorizedUser',
    'authenticatedUserName',
    'sessionToken',
    'recoveryToken',
    'accessCode',
    'jwtToken',
    'memberAccessToken',
    'accessToken',
    'idToken',
  ]

  return sanitizeObject(source, targetKeys) as IMkAuthorizedSession
}
